import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import highchartsHeatmap from 'highcharts/modules/heatmap';
import highchartsBoost from 'highcharts/modules/boost';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { compare_name_asc } from '../../utils/utils';

// Shared axis config so the "Cleaning Schedules" heatmap and the stacked "Soiling Ratio"
// chart use the exact same calendar: identical plot margins (columns aligned pixel-for-pixel),
// identical datetime tick interval, and a real time axis (same min/max => same ticks).
const DAY_MS = 24 * 3600 * 1000;
export const ALIGNED_CHART_MARGIN_LEFT = 120;
export const ALIGNED_CHART_MARGIN_RIGHT = 40;
export const ALIGNED_CHART_TICK_INTERVAL = 30 * DAY_MS;

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
highchartsHeatmap(Highcharts);
highchartsBoost(Highcharts);
noDataToDisplay(Highcharts);

dayjs.extend(dayOfYear);

type Props = {
  data: any;
  rain_dates: any;
};

const CompareCleaningSchedules = ({ data, rain_dates }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);
  const chartComponent = useRef(null);
  const isLoading = false;

  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort(compare_name_asc);
  }, [data]);

  const categories = useMemo(() => {
    return data?.[0]?.data?.daily?.times || [];
  }, [data]);

  const preparedData = useMemo(() => {
    if (!data || !categories.length) return null;

    let timeseries_data: any[][] = [];
    let rain_data: any[][] = [];

    sortedData.forEach((item: any, y: number) => {
      const days_to_clean = Math.max(parseInt(item.time_to_clean), 1);

      if (item?.cleaning_graph && Array.isArray(item.cleaning_graph)) {
        item.cleaning_graph.forEach((value: number, index: number) => {
          if (value !== 0) {
            timeseries_data.push([index, y, value]);
          }
        });
      } else {
        item?.schedule.forEach((date: string, index: number) => {
          let d = new Date(Date.parse(date));

          let startIndex = categories.findIndex((catDate: any) => catDate === date);

          if (startIndex === -1) {
            const targetTime = d.getTime();
            let closestIndex = -1;
            let minDiff = Infinity;

            categories.forEach((catDate: any, catIndex: any) => {
              const catTime = new Date(catDate).getTime();
              const diff = Math.abs(catTime - targetTime);
              if (diff < minDiff) {
                minDiff = diff;
                closestIndex = catIndex;
              }
            });

            if (minDiff < 7 * 24 * 60 * 60 * 1000) {
              startIndex = closestIndex;
            }
          }

          if (startIndex === -1) return;

          const isEvenCleaning = index % 2 === 0;
          const cleaningValue = isEvenCleaning ? 1 : 0.5;

          for (let i = 0; i < days_to_clean; i++) {
            timeseries_data.push([startIndex + i, y, cleaningValue]);
          }
        });
      }

      rain_dates?.forEach((date: string) => {
        let d = new Date(Date.parse(date));

        let rainIndex = categories.findIndex((catDate: any) => catDate === date);

        if (rainIndex === -1) {
          const targetTime = d.getTime();
          let closestIndex = -1;
          let minDiff = Infinity;

          categories.forEach((catDate: any, catIndex: any) => {
            const catTime = new Date(catDate).getTime();
            const diff = Math.abs(catTime - targetTime);
            if (diff < minDiff) {
              minDiff = diff;
              closestIndex = catIndex;
            }
          });

          if (minDiff < 7 * 24 * 60 * 60 * 1000) {
            rainIndex = closestIndex;
          }
        }

        if (rainIndex !== -1) {
          rain_data.push([rainIndex, y, 5]);
        }
      });
      if (rain_data.findIndex((d) => d[0] === 0) === -1) rain_data.push([0, 0, null]);
      if (rain_data.findIndex((d) => d[0] === categories.length - 1) === -1) rain_data.push([categories.length - 1, 0, null]);
    });

    // Map category indices to real timestamps so the heatmap shares a datetime axis with the
    // soiling ratio chart (same calendar on both x-axes).
    const toMs = (idx: number) => Date.parse(categories[Math.min(Math.max(idx, 0), categories.length - 1)]);
    const toTimestamps = (rows: any[][]) => rows.map(([x, y, v]) => [toMs(x), y, v]);

    return [
      {
        name: 'Cleaning',
        data: toTimestamps(timeseries_data),
        color: 'orange',
      },
      {
        name: 'Rain',
        data: toTimestamps(rain_data),
        color: 'blue',
      },
    ];
  }, [data, rain_dates, categories, sortedData]);

  useEffect(() => {
    if (preparedData) {
      prepareHighcharts(preparedData);
    }
  }, [preparedData, isDarkMode, sortedData]);

  const prepareHighcharts = (highChartsData: any) => {
    const chart: Highcharts.Options = {
      chart: {
        type: 'heatmap',
        height: 170 + 25 * data?.length,
        marginTop: 50,
        marginBottom: 120,
        marginLeft: ALIGNED_CHART_MARGIN_LEFT,
        marginRight: ALIGNED_CHART_MARGIN_RIGHT,
        // @ts-expect-error Highcharts type missing zoomType in this setup
        zoomType: 'x',
      },
      title: {
        text: 'Cleaning Schedules',
        align: 'left',
      },
      boost: {
        useGPUTranslations: true,
        usePreallocated: true,
      },
      legend: {
        enabled: true,
        itemMarginBottom: -20,
      },
      xAxis: {
        type: 'datetime',
        title: { text: 'Date' },
        min: categories.length ? Date.parse(categories[0]) : undefined,
        max: categories.length ? Date.parse(categories[categories.length - 1]) : undefined,
        tickInterval: ALIGNED_CHART_TICK_INTERVAL,
        labels: {
          formatter: function (): string {
            // value is a timestamp on a datetime axis
            const d = new Date((this as any).value);
            return ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2);
          },
        },
        gridLineWidth: 1,
      },
      yAxis: {
        categories: sortedData.map((item: any) => item.name),
        title: { text: '' },
      },
      tooltip: {
        formatter: function (): string {
          // @ts-ignore - Highcharts formatter context
          const point = this.point;
          // @ts-ignore - Highcharts formatter context
          const series = this.series;
          // On a datetime axis point.x is a timestamp.
          let d = new Date(point.x);

          // Show year in tooltip when multiple solutions are selected
          const dateFormat =
            data && data.length > 1
              ? d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
              : d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2);

          // Check the value to determine the event type
          let eventType = series.name;
          if (point.value === 3) {
            eventType = 'Blacklist';
          } else if (point.value === 4) {
            eventType = 'Special Cleaning';
          }

          return series.yAxis.categories[point.y || 0] + '<br>' + eventType + ' on ' + dateFormat;
        },
      },
      colorAxis: {
        min: 0,
        max: 5,
        stops: [
          [0, '#FFFFFF'],
          [0.2, '#FF6B35'],
          [0.4, '#C55824'],
          [0.6, '#808080'],
          [0.8, '#b75cf8ff'],
          [1, '#0066CC'],
        ],
        showInLegend: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          turboThreshold: 10000,
          animation: {
            duration: 500,
          },
        },
        heatmap: {
          enableMouseTracking: true,
          colsize: DAY_MS,
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          data &&
          chartOptions && (
            <>
              <HighchartsReact
                ref={chartComponent}
                containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
                highcharts={Highcharts}
                options={chartOptions}
              />
            </>
          )
        )}
      </Box>
    </>
  );
};

export default CompareCleaningSchedules;
